/* Types */
export interface GraphQlErrorLocation {
  column: number;
  line: number;
  sourceName: string | null;
}

export interface GraphQlError {
  path: string[];
  data: string | null;
  errorType: string;
  errorInfo: string | null;
  locations: GraphQlErrorLocation[];
  message: string;
}

export enum SampleTypeCodeEnum {
  WHOLE = 'WHOLE',
  PLASMA = 'PLASMA',
  SERUM = 'SERUM',
}

export enum ShipmentTypeEnum {
  SEQUENCING = 'SEQUENCING',
  STORAGE = 'STORAGE',
}

export enum ShipmentStatusEnum {
  STAGED = 'STAGED',
  MANIFESTED = 'MANIFESTED',
  SHIPPED = 'SHIPPED',
}

export enum SampleTypeNameEnum {
  WHOLE = 'Whole Blood',
  PLASMA = 'Plasma Blood',
  SERUM = 'Serum Blood',
}

export enum AliquotStatus {
  SAT,
  CLT,
  CTM,
  HEM,
  SHV,
  COC,
  OTH,
}

export enum SampleStatus {
  SAT,
  EQF,
  LBE,
  PST,
  CLT,
  CTM,
  HEM,
  ICT,
  LIP,
  LYS,
  SNP,
}

export enum ReportExportStatus {
  sequencing = 'sequencing',
  storage = 'storage',
}

/* Payloads */

export interface CreateSampleBatchInput {
  samples: any;
  sampleType: SampleTypeCodeEnum | null;
}

export type CreateCryoBoxInput = {
  aliquotPositions: string[];
  bioBanked: boolean;
  cryoboxBarcode?: string;
  freezerShelfNo: string;
  freezerId: string;
  height: string;
  rackPositionNo: string;
  sampleType: SampleTypeCodeEnum;
  serialNo: string;
  shelfRackNo: string;
  width: string;
};

export interface FindSampleBatchPayload {
  searchTerm?: string | null;
}

export interface LinkRGCBarcodeInput {
  aliquotId: string;
  rgcBarcode: string;
}

export type UpdateCryoBoxInput = {
  aliquotPositions: string[];
  bioBanked: boolean;
  cryoboxBarcode: string;
  freezerId: string;
  freezerShelfNo: string;
  rackPositionNo: string;
  cryoboxUuid: string;
  shelfRackNo: string;
};

/* Interfaces */
export interface Aliquot {
  barcode: string;
  createdBy: string;
  createdTs: string;
  lastUpdatedBy: string;
  mismatch: boolean;
  sampleBarcode: string;
  sampleBatchId: string;
  status: AliquotStatus;
  updatedTs: string;
  volume: number;
}

export interface CryoboxSearchResult {
  data: Cryobox[];
  count: number;
}

export interface CreateStagedShipmentResult {
  stagedShipmentMetaUuid: string;
}

export interface MoveStagedAliquotResult {
  aliquotUuid: string;
}

export interface FinaliseShipmentResult {
  shipmentUuid: string;
}

export interface AddCryoboxToStagingResult {
  cryoboxUuid: string;
}

export interface AddCryoboxToStagingInput {
  cryoboxBarcode: string | null;
  cryoboxUuid: string | null;
}

export interface RemoveCryoboxFromStagingResult {
  cryoboxUuid: string;
}

export interface RemoveCryoboxFromStagingInput {
  cryoboxUuid: string;
}

export interface CreateStagedShipmentInput {
  destination: string;
  shipmentType: ShipmentTypeEnum | '' | string;
  plannedShipmentDate: string;
}

export interface MoveStagedAliquotInput {
  aliquotUuid: string | '';
  updatedCryoboxBarcode: string | '';
  updatedCryoboxPosition: number | null;
}

export interface FinaliseShipmentInput {
  shipmentId: string | '';
  shippedDate: string | '';
}

export interface Cryobox {
  aliquotPositions: string[];
  aliquotPositionTimestamps?: string[];
  barcodePositions?: string[];
  barcodeLinkTimestamps?: string[];
  bioBanked: boolean;
  cryoboxBarcode?: string;
  createdBy: string;
  createdTs: string;
  freezerShelfNo: string;
  freezer: Freezer;
  height: number;
  locked: boolean;
  lastUpdatedBy: string;
  rackPositionNo: string;
  sampleType: SampleTypeCodeEnum;
  serialNo: string;
  cryoboxUuid: string;
  shelfRackNo: string;
  updatedTs: string;
  width: number;
}

export interface Freezer {
  freezerId: string;
  name: string;
  serialNo: string;
  temp: string;
}

export interface Sample {
  barcode: string;
  status: SampleStatus;
  volume: number;
  aliquots: Aliquot[];
}

export interface SampleBatch {
  batchId: string;
  sampleType: SampleTypeCodeEnum;
  samples: [Sample];
  createdTs: string;
  updatedTs: string;
  createdBy: string;
  lastUpdatedBy: string;
}

export interface SampleBatchLookup {
  batchId: string;
  sampleType: SampleTypeCodeEnum;
  createdTs: string;
}

export interface ExportStatus {
  runId: string;
  jobStatus: string;
  jobError?: string;
  createdTs: string;
  updatedTs?: string;
  exportLink?: string;
}

/* Response */
export type CreateCryoboxResponse = {
  data?: { createCryobox: Cryobox };
  errors?: GraphQlError[];
};

export type CreateSampleBatchResponse = {
  data?: { createSampleBatch: SampleBatch };
  errors?: GraphQlError[];
};

export type FindCryoboxesResponse = {
  data?: {
    findCryoboxes: CryoboxSearchResult;
  };
  errors?: GraphQlError[];
};

export type FindSampleBatchesResponse = {
  data?: { findSampleBatches: SampleBatchLookup[] };
  errors?: GraphQlError[];
};

export type GetCryoboxResponse = {
  data?: { getCryobox: Cryobox };
  errors?: GraphQlError[];
};

export type GetSampleBatchResponse = {
  data?: { getSampleBatch: SampleBatch };
  errors?: GraphQlError[];
};

export type LinkRGCBarcodeResponse = {
  data?: { linkRGCBarcode: Freezer[] };
  errors?: GraphQlError[];
};

export type ListFreezersResponse = {
  data?: { listFreezers: Freezer[] };
  errors?: GraphQlError[];
};

export type UpdateCryoboxResponse = {
  data?: { updateCryobox: Cryobox };
  errors?: GraphQlError[];
};

export type RequestSampleResponse = {
  data?: { sampleExportRequest: { runId: string } };
  errors?: GraphQlError[];
};

export type SampleExportStatusResponse = {
  data?: { getSampleExportStatus: ExportStatus };
  errors?: GraphQlError[];
};

export type CreateStagedShipmentResponse = {
  data?: { createStagedShipment: CreateStagedShipmentResult };
  errors?: GraphQlError[];
};

export type MoveStagedAliquotResponse = {
  data?: { moveStagedAliquot: CreateStagedShipmentResult };
  errors?: GraphQlError[];
};

export type FinaliseShipmentResponse = {
  data?: { shipManifestedShipment: FinaliseShipmentResult };
  errors?: GraphQlError[];
};

export type AddCryoboxToStagingResponse = {
  data?: { addCryoboxToStaging: AddCryoboxToStagingResult };
  errors?: GraphQlError[];
};

export type RemoveCryoboxFromStagingResponse = {
  data?: { removeCryoboxFromStaging: RemoveCryoboxFromStagingResult };
  errors?: GraphQlError[];
};

export interface AliquotLocation {
  aliquot_location_uuid: string;
  location: string;
  description: string;
  shippable: boolean;
}

export type AliquotLocationsResponse = {
  data?: { listAliquotLocations: AliquotLocation[] };
  errors?: GraphQlError[];
};

export interface StagedShipmentMeta {
  plannedShipmentDate: string;
  destinationLocation: string;
  destinationLocationUuid: string;
  shipmentType: string;
}

export type GetStagedShipmentMetaResponse = {
  data?: { getStagedShipmentMeta: StagedShipmentMeta };
  errors?: GraphQlError[];
};

export interface Freezer {
  freezerId: string;
  serialNo: string;
  name: string;
  temp: string;
}

export interface CryoboxDetails {
  serialNo: string;
  aliquotCount: number;
  freezer: Freezer;
  freezerShelfNo: number;
  shelfRackNo: number;
  rackPositionNo: number;
  cryoboxUuid: string;
  cryoboxBarcode: string;
  eligible: boolean;
  eligibleCount: number;
  ineligibleCount: number;
}
export interface StagedShipment {
  cryoboxes: CryoboxDetails[];
  totalAliquotCount: number;
  plannedShipmentDate: string;
  shipmentType: ShipmentTypeEnum;
  status?: string;
}

export interface CryoboxSummary {
  serialNo: string;
  aliquotCount: number;
  freezer: Freezer;
  freezerShelfNo: number;
  shelfRackNo: number;
  rackPositionNo: number;
  cryoboxUuid: string;
  cryoboxBarcode: string;
  sampleType: SampleTypeCodeEnum;
}
export interface Shipment {
  shipmentId: string;
  displayId: string;
  shipmentType: ShipmentTypeEnum;
  source: string;
  destination: string;
  status: ShipmentStatusEnum;
  manifestDate: string;
  shippedDate: string;
  cryoboxes: CryoboxSummary[];
  totalAliquotCount: number;
  plannedShipmentDate: string;
  createdTs: string;
}

export type GetStagedShipmentResponse = {
  data?: { getStagedShipment: StagedShipment[] };
  errors?: GraphQlError[];
};

export type ListShipmentsResponse = {
  data?: { listShipments: Shipment[] };
  errors?: GraphQlError[];
};

export type GetShipmentResponse = {
  data?: { getShipment: Shipment };
  errors?: GraphQlError[];
};

export type GetShipmentManifestResponse = {
  data?: { getShipmentManifest: ShipmentManifestLink };
  errors?: GraphQlError[];
};

export interface ResetStagedShipment {
  stagedShipmentMetaUuid: string;
}
export type ResetStagedShipmentResponse = {
  data?: { resetStagedShipment: ResetStagedShipment };
  errors?: GraphQlError[];
};

export interface ManifestStagedShipment {
  shipmentId: string;
}

export type manifestStagedShipmentResponse = {
  data?: { manifestStagedShipment: ManifestStagedShipment };
  errors?: GraphQlError[];
};

export interface RankedCryobox {
  cryoboxUuid: string;
  serialNo: string;
  eligible: boolean;
  eligibleCount: number;
  ineligibleCount: number;
  freezerName: string;
  freezerShelfNo: number;
  shelfRackNo: number;
  rackPositionNo: number;
  rank: number;
  sampleType: SampleTypeCodeEnum;
}

export interface PickListAliquot {
  cryoboxUuid: string;
  aliquotUuid: string;
  aliquotBarcode: string;
  aliquotPosition: string;
  cryoboxSerialNo: string;
  cryoboxBarcode: string;
  freezerName: string;
  freezerShelfNo: number;
  freezerShelfRackNo: number;
  freezerRackPositionNo: number;
  reason: string;
  eligible: boolean;
  shippable: boolean;
  confirmSampleId?: string;
  newBoxBarcode?: string;
  newSamplePosition?: number;
}

export interface ShipmentManifestLink {
  link: string;
}

export type GetRankedCryoboxesResponse = {
  data?: { getRankedCryoboxes: RankedCryobox[] };
  errors?: GraphQlError[];
};

export type GetStagingPickListResponse = {
  data?: { getStagingPickList: PickListAliquot[] };
  errors?: GraphQlError[];
};
